import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import _ from 'lodash';
import {
  ModesOfOperation,
  MotionMasterMessage,
  OffsetDetectionStatus,
  OsCommandResponse,
  SystemIdentificationOsCommandSignalType,
  SystemIdentificationOsCommandStartProcedure,
  factoryResetDefaultFilesToKeep,
  isCalibrationFirmware,
  makeDeviceParameterId,
  parsePlantModelCsv,
} from 'motion-master-client';
import { ToastrService } from 'ngx-toastr';
import { EMPTY, Observable, defer, from, fromEvent, iif, lastValueFrom, of } from 'rxjs';
import {
  catchError,
  concatAll,
  concatMap,
  delay,
  distinctUntilChanged,
  exhaustMap,
  filter,
  first,
  map,
  mergeMap,
  takeWhile,
  tap,
  timeout,
  withLatestFrom,
} from 'rxjs/operators';
import semver from 'semver';

import {
  clearDeviceFiles,
  getCoggingTorqueDataRequest,
  getDeviceFileRequest,
  getDeviceFileStatus,
  setDeviceFileRequest,
} from './device-file.actions';
import {
  addDeviceParameters,
  assignFeaturesToParameters,
  clearDeviceParameters,
  deviceParameterValuesLoaded,
  getDeviceParameters,
  getDeviceParameterValuesRequest,
  getDeviceParameterValuesStatus,
  loadDeviceParameterValues,
  updateDeviceParameters,
  upsertDeviceParameters,
} from './device-parameter.actions';
import {
  DeviceParameter,
  deviceParameterValuesHaveErrors,
  evasParameterValues,
  fillDevicePdoParametersWithMappingEntries,
} from './device-parameter.model';
import { selectAllDeviceParameters } from './device-parameter.selectors';
import {
  addDevice,
  circuloEncoderConfigurationProcedureCompleted,
  circuloEncoderCalibrationProcedureCompleted,
  clearDevices,
  computePositionAutoTuningGainsRequest,
  computePositionAutoTuningGainsStatus,
  computeVelocityAutoTuningGainsRequest,
  computeVelocityAutoTuningGainsStatus,
  disableMotionControllerRequest,
  disableMotionControllerStatus,
  enableMotionControllerRequest,
  enableMotionControllerStatus,
  factoryResetRequest,
  factoryResetStatus,
  getConfigurationFileParametersRequest,
  getDeviceEthercatAlStateRequest,
  getDevicesRequest,
  getDevicesStatus,
  integroEncoderCalibrationProcedureRunning,
  loadDeviceConfigurationFromFileRequest,
  loadDeviceConfigurationFromFileStatus,
  loadDeviceSmmFirmwareVersion,
  loadDeviceSmmMaxPositionDiscrepancy,
  loadDeviceSmmSerialNumber,
  loadDeviceSmmTemperature,
  readEncoderRegisterCompleted,
  readEncoderRegisterRequest,
  reloadDeviceStateMachineRequest,
  reloadDeviceStateMachineStatus,
  requestControllerAutoTuning,
  resetDeviceFaultRequest,
  resetDeviceFaultStatus,
  saveConfigurationToDeviceRequest,
  saveConfigurationToDeviceStatus,
  startCirculoEncoderConfigurationRequest,
  startCirculoEncoderConfigurationStatus,
  startCirculoEncoderNarrowAngleCalibrationProcedureRequest,
  startCirculoEncoderNarrowAngleCalibrationProcedureStatus,
  startCoggingTorqueRecordingRequest,
  startCoggingTorqueRecordingStatus,
  startDeviceFirmwareInstallationRequest,
  startDeviceFirmwareInstallationStatus,
  startFullAutoTuningRequest,
  startFullAutoTuningStatus,
  startLimitedRangeSystemIdentificationNotification,
  startLimitedRangeSystemIdentificationRequest,
  startLimitedRangeSystemIdentificationStatus,
  startOffsetDetectionOldRequest,
  startOffsetDetectionOldStatus,
  startOpenLoopFieldControlRequest,
  startOpenLoopFieldControlStatus,
  startSignalGeneratorRequest,
  startSignalGeneratorStatus,
  startSystemIdentificationRequest,
  startSystemIdentificationStatus,
  stopFullAutoTuningRequest,
  stopFullAutoTuningStatus,
  stopSignalGeneratorRequest,
  stopSignalGeneratorStatus,
  triggerCirculoRegisterReadAfterWritingEncoderConfigBin,
  updateDevice,
  updateDeviceConfigurationFileEntriesRequest,
  updateDeviceConfigurationFileEntriesStatus,
  updateDeviceInfoLoading,
  updateDeviceParametersFromConfig,
  updateDeviceSmmParameterStructureVersion,
  updateSystemIdentificationParametersValuesFromPlantModel,
  writeCirculoEncoderConfigFailed,
} from './device.actions';
import { Device } from './device.model';
import { selectAllDevices } from './device.selectors';
import { loadErrorReportsRequest } from './error-report.actions';
import { updateMotionMasterAlive, updateMotionMasterState } from './global.actions';
import { mapParametersToFieldNameValues } from './parameter.model';
import { updatePreviousFirmwareVersions } from './setting.actions';
import { updateSystemIdentificationParameters } from './system-identification-parameter.actions';
import { updateTuningControllerParameters } from './tuning-controller-parameter.actions';

import { AppState } from './index';

import { allBaseParameterValuesLoaded$, retryWithDelay } from '@app/custom-operators';
import { IssueType } from '@app/models/issue';
import {
  ProcedureStatusSuccessCode,
  circuloEncoderConfigurationCompleted,
  circuloEncoderNarrowAngleCalibrationProcedureCompleted,
  circuloEncoderNarrowAngleCalibrationProcedureEnded,
  coggingTorqueRecordingCompleted,
  deviceCia402StateCompleted,
  deviceCia402StateRunning,
  deviceParameterValuesEnded,
  limitedRangeSystemIdentificationCompleted,
  limitedRangeSystemIdentificationEnded,
  positionAutoTuningCompleted,
  positionFullAutoTuningCompleted,
  procedureStatusCompleted,
  systemIdentificationCompleted,
  systemIdentificationEnded,
  velocityAutoTuningCompleted,
  velocityFullAutoTuningCompleted,
} from '@app/models/status-resolve';
import { BrandService } from '@app/services/brand.service';
import { ConnectionService } from '@app/services/connection.service';
import { DeviceService } from '@app/services/device.service';
import { GlobalService } from '@app/services/global.service';
import { MonitoringService } from '@app/services/monitoring.service';
import { TuningControllerService } from '@app/services/tuning-controller.service';
import { getCurrentValue } from '@app/util';

@Injectable()
export class DeviceEffects {
  private actions$ = inject(Actions);
  private router = inject(Router);
  private cn = inject(ConnectionService);
  private monitoringService = inject(MonitoringService);
  private brandService = inject(BrandService);
  private deviceService = inject(DeviceService);
  private globalService = inject(GlobalService);
  private store$ = inject<Store<AppState>>(Store);
  private tuningControllerService = inject(TuningControllerService);
  private toastr = inject(ToastrService);

  /**
   * Increase the system client timeout.
   */
  requestSetSystemClientTimeoutOnMotionMasterAlive$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(updateMotionMasterAlive),
        filter((action) => action.alive),
        withLatestFrom(this.store$),
        tap(([_action, state]) => {
          this.cn.client.request
            .setSystemClientTimeout({ timeoutMs: state.setting.connectionConfig.clientAliveTimeout })
            .subscribe();
        }),
      ),
    { dispatch: false },
  );

  /**
   * Requests device information when Motion Master becomes alive.
   *
   * This is the first step in the loading sequence, retrieving the list of devices.
   *
   * The `alive` flag is set to true when the Motion Master process restarts
   * or when the browser is reloaded.
   */
  requestGetDevicesOnMotionMasterAlive$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateMotionMasterAlive),
      filter((action) => action.alive),
      map(() => getDevicesRequest({ timeout: 5000, suppressToastr: true })),
    ),
  );

  /**
   * Clear toastr notifications from the previous _life_ of Motion Master.
   */
  clearToastrOnMotionMasterAlive$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(updateMotionMasterAlive),
        filter((action) => action.alive),
        tap(() => this.toastr.clear()),
      ),
    { dispatch: false },
  );

  /**
   * Show the page "Please ensure that Motion Master is running" when Motion Master is not alive anymore.
   */
  navigateOnMotionMasterNotAlive$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(updateMotionMasterAlive),
        filter((action) => !action.alive),
        tap(() => this.router.navigate(['/start'])),
      ),
    { dispatch: false },
  );

  /**
   * Clears application state when Motion Master is no longer alive.
   *
   * If the Motion Master process is killed, it does not emit the
   * DEINITIALIZING system event. As a result, the
   * `clearStateOnMotionMasterStateDeinitializing` effect is not triggered,
   * so state cleanup must be handled here.
   *
   * A zero-delay is introduced to allow Angular component lifecycle hooks
   * (ngOnDestroy) to run first, ensuring subscriptions are properly
   * cleaned up before the state is cleared.
   */
  clearStateOnMotionMasterNotAlive$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateMotionMasterAlive),
      filter((action) => !action.alive),
      delay(0), // let components lifecycle (ngOnDestroy) execute before clearing - it unsubscribes from possibly undefined values
      mergeMap(() => [clearDeviceFiles(), clearDeviceParameters(), clearDevices()]),
    ),
  );

  /**
   * Stops all active monitoring when Motion Master is no longer alive.
   *
   * Monitoring is only meaningful while Motion Master is running,
   * so all subscriptions are explicitly terminated when it goes down.
   */
  stopMonitoringAllOnMotionMasterNotAlive$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(updateMotionMasterAlive),
        filter((action) => !action.alive),
        tap(() => this.cn.client.stopMonitoringAll()),
      ),
    { dispatch: false },
  );

  /**
   * Clears application state when Motion Master enters the DEINITIALIZING state.
   *
   * This occurs during process shutdown or re-initialization, ensuring that
   * all device-related state is reset in a controlled manner.
   *
   * A zero-delay is introduced to allow Angular component lifecycle hooks
   * (ngOnDestroy) to run first, ensuring subscriptions are properly
   * cleaned up before the state is cleared.
   */
  clearStateOnMotionMasterStateDeinitializing$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateMotionMasterState),
      filter((action) => action.state === MotionMasterMessage.Status.SystemEvent.State.DEINITIALIZING),
      delay(0), // let components lifecycle (ngOnDestroy) execute before clearing - it unsubscribes from possibly undefined values
      mergeMap(() => [clearDeviceFiles(), clearDeviceParameters(), clearDevices()]),
    ),
  );

  /**
   * Stops all active monitoring when Motion Master enters the DEINITIALIZING state.
   *
   * Deinitialization occurs during process shutdown or re-initialization,
   * such as after a firmware update or when the device topology changes
   * (e.g. number of slaves on the network).
   *
   * Monitoring is explicitly stopped to avoid stale subscriptions and
   * invalid data streams during the transition.
   */
  stopMonitoringAllOnMotionMasterStateDeinitializing$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(updateMotionMasterState),
        filter((action) => action.state === MotionMasterMessage.Status.SystemEvent.State.DEINITIALIZING),
        tap(() => this.cn.client.stopMonitoringAll()),
      ),
    { dispatch: false },
  );

  /**
   * Requests device loading when Motion Master reaches the INITIALIZED state.
   *
   * After a topology change (e.g. number of devices) or during startup,
   * Motion Master transitions through its states and eventually becomes INITIALIZED.
   * Devices are cleared when deinitialization begins and must be reloaded once
   * initialization completes.
   *
   * On first startup, device loading may already be in progress, so this effect
   * avoids triggering a duplicate request by checking that no loading is ongoing
   * and that the device list is still empty.
   */
  requestGetDevicesOnMotionMasterStateInitialized$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateMotionMasterState),
      filter((action) => action.state === MotionMasterMessage.Status.SystemEvent.State.INITIALIZED),
      withLatestFrom(this.store$),
      filter(([_action, state]) => !state.devices.deviceInfoLoading && state.devices.ids.length === 0),
      map(() => getDevicesRequest({ timeout: 5000 })),
    ),
  );

  /**
   * Handles navigation based on Motion Master state transitions.
   *
   * - INITIALIZING: navigates to root to reset UI during startup
   * - DEINITIALIZING: navigates to root to clear UI during shutdown
   * - DEINITIALIZED: navigates to an issue page indicating system shutdown
   * - WAITING_FOR_SLAVES: navigates to an issue page indicating missing/late slaves
   *
   * This ensures the UI always reflects the current system state and
   * guides the user to the appropriate context or error view.
   */
  navigateOnUpdateMotionMasterState$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(updateMotionMasterState),
        map((action) => action.state),
        tap((state) => {
          if (state === MotionMasterMessage.Status.SystemEvent.State.INITIALIZING) {
            this.router.navigateByUrl('/');
          } else if (state === MotionMasterMessage.Status.SystemEvent.State.DEINITIALIZING) {
            this.router.navigateByUrl('/');
          } else if (state === MotionMasterMessage.Status.SystemEvent.State.DEINITIALIZED) {
            this.router.navigate(['/issues', IssueType.MOTION_MASTER_DEINITIALIZED]);
          } else if (state === MotionMasterMessage.Status.SystemEvent.State.WAITING_FOR_SLAVES) {
            this.router.navigate(['/issues', IssueType.MOTION_MASTER_WAITING_FOR_SLAVES]);
          }
        }),
      ),
    { dispatch: false },
  );

  //
  // Load the devices.
  //

  updateDevicesLoadingOnGetDevicesRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getDevicesRequest),
      map(() => updateDeviceInfoLoading({ deviceInfoLoading: true })),
    ),
  );

  handleGetDevicesRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getDevicesRequest),
      mergeMap((action) =>
        this.cn.client.request.getDevices(action.timeout).pipe(
          map((devices) => getDevicesStatus({ deviceInfo: { devices } })),
          catchError(() => {
            const { suppressToastr } = action;
            return of(
              getDevicesStatus({
                deviceInfo: {
                  timeout: {
                    message: 'Timeout while getting the device info.',
                  },
                },
                suppressToastr,
              }),
            );
          }),
        ),
      ),
    ),
  );

  handleGetDevicesStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getDevicesStatus),
      tap((action) => this.globalService.toastrStatusMessage(action.deviceInfo, 'Device Info', action.suppressToastr)),
      map(() => updateDeviceInfoLoading({ deviceInfoLoading: false })),
    ),
  );

  addDevices$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getDevicesStatus),
      map(({ deviceInfo }) => deviceInfo.devices?.map((device: Device) => addDevice({ device })) ?? []),
      concatAll(),
    ),
  );

  /**
   * Navigate to the devices list when the device info contains at least one device.
   * Otherwise navigate to page "Please ensure that your devices are connected".
   */
  navigateOnGetDeviceInfoStatus$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(getDevicesStatus),
        tap((action) => {
          const { deviceInfo } = action;
          if (deviceInfo.devices?.length > 0) {
            this.router.navigate(['/devices']);
          } else {
            this.router.navigate(['/issues', IssueType.NO_DEVICES]);
          }
        }),
      ),
    { dispatch: false },
  );

  /**
   * Load error reports after device info has been received. There is no reason to fetch error reports if there are no devices.
   * Also, if there's error loading/parsing reports JSON the error toastr will not be cleared,
   * unlike if the request to load error reports was dispatched in the App component.
   */
  requestLoadErrorReportsOnGetDeviceInfoStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getDevicesStatus),
      filter((action) => action.deviceInfo?.devices?.length > 0),
      map(() => loadErrorReportsRequest()),
    ),
  );

  //
  // Resolve device product image.
  //

  resolveDeviceProductImageOnAddDevice$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(addDevice),
        concatMap(({ device }) => {
          if (device.hardwareDescription) {
            return this.brandService.getProductImage(device).pipe(
              first(),
              map((svg) => {
                if (!svg) {
                  throw new Error('Fetched product image (SVG) is empty.');
                }
                return updateDevice({
                  update: {
                    changes: {
                      productImage: svg,
                    },
                    id: device.serialNumber,
                  },
                });
              }),
              catchError((_err) => {
                console.warn(
                  `No product image is found for the device ${device.serialNumber} on S3. Fallback to loading an image from the device.`,
                );
                return this.getStackImageFromDeviceAndMapToUpdateProductImage(device);
              }),
            );
          } else {
            console.warn(
              'Device has no hardware description required for fetching its product image from S3. Fallback to loading an image from the device.',
            );
            return this.getStackImageFromDeviceAndMapToUpdateProductImage(device);
          }
        }),
      ),
    { dispatch: true },
  );

  //
  // Load device parameters.
  //

  getBaseDeviceParameterValuesOnAddDeviceParameters$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addDeviceParameters),
      mergeMap((action) => {
        const { type, device, deviceParameters, loadFromCache = true } = action;

        // Requesting values for a device that has no parameters in the store yields values that cannot be
        // matched to a parameter entity, so skip the request. The device still has to be marked as
        // initialized here because markDeviceInitializedOnBaseParameterValuesLoaded$ waits for the values.
        if (deviceParameters.length === 0) {
          return of(
            updateDevice({
              update: {
                changes: {
                  initialized: true,
                },
                id: device.serialNumber,
              },
            }),
            getDeviceEthercatAlStateRequest({ device, timeout: 5000 }),
          );
        }

        const parameters = [
          { index: 0x100a, subindex: 0, loadFromCache }, // Manufacturer software version
          { index: 0x2001, subindex: 0, loadFromCache }, // Commutation angle offset
          { index: 0x20f2, subindex: 0, loadFromCache }, // Assigned name
          { index: 0x200e, subindex: 1, loadFromCache }, // Device logging
        ];

        return of(
          getDeviceParameterValuesRequest({
            device,
            initiator: type,
            parameters,
            sendProgress: false,
            timeout: 10000,
          }),
        );
      }),
    ),
  );

  requestGetDeviceParametersOnAddDevice$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addDevice),
      map((action) =>
        getDeviceParameters({
          device: action.device,
          timeout: 30000, // 30 seconds for parameter info and value retrieval
          suppressToastr: true,
          initiator: addDevice.type,
        }),
      ),
    ),
  );

  handleGetDeviceParametersOnAddDevice$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getDeviceParameters),
      concatMap((action) => {
        return this.cn.client.request
          .getDeviceParameters(
            {
              deviceAddress: action.device.deviceAddress,
              extendWithEsi: true,
              skipReadingValues: true,
            },
            action.timeout, // 30 seconds for parameter info and value retrieval
          )
          .pipe(
            map(({ parameters }) => {
              // Keep the Error reported by the client out of the store as it is not serializable, but keep its
              // message so the UI can tell that the metadata of this parameter is missing from the ESI file.
              const deviceParameters = (parameters as DeviceParameter[]).map(({ esiExtensionError, ...parameter }) => ({
                ...parameter,
                deviceSerialNumber: action.device.serialNumber,
                devicePosition: action.device.position,
                ...(esiExtensionError ? { esiExtensionErrorMessage: esiExtensionError.message } : {}),
              }));

              const esiExtensionErrorCount = deviceParameters.filter((p) => p.esiExtensionErrorMessage).length;
              if (esiExtensionErrorCount > 0) {
                console.warn(
                  `${esiExtensionErrorCount} of ${deviceParameters.length} parameters of device ${action.device.serialNumber} could not be extended with the metadata from the ESI file. The ESI file most likely does not match the firmware installed on this device.`,
                );
              }

              return addDeviceParameters({
                device: action.device,
                deviceParameters,
              });
            }),
            catchError((err) => {
              console.warn(
                `Failed to load device parameters for device ${action.device.serialNumber}. Error: ${err.message}`,
              );

              if (!action.suppressToastr) {
                this.toastr.error(err.message, 'Failed to Read Device Parameters');
              }

              // Record the failure on the device so the UI can tell an unreadable parameter catalog apart
              // from a device that has no firmware installed - without it the empty catalog leaves the
              // firmware version unknown and the device is reported as having no firmware.
              return of(
                updateDevice({
                  update: {
                    changes: {
                      parameterLoadError: err.message,
                    },
                    id: action.device.serialNumber,
                  },
                }),
                addDeviceParameters({ device: action.device, deviceParameters: [] }),
              );
            }),
          );
      }),
    ),
  );

  // Wait for the base parameter values (including manufacturerSoftwareVersion) to finish loading before
  // marking the device as initialized. Setting initialized earlier — on addDeviceParameters — caused a
  // brief flash of the "no firmware" warning because hasFirmware is derived from manufacturerSoftwareVersion,
  // which is still empty while the base values request is in flight.
  markDeviceInitializedOnBaseParameterValuesLoaded$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getDeviceParameterValuesStatus),
      filter(
        ({ initiator, deviceParameterValues }) =>
          initiator === addDeviceParameters.type && deviceParameterValuesEnded(deviceParameterValues),
      ),
      mergeMap(({ device }) =>
        of(
          updateDevice({
            update: {
              changes: {
                initialized: true,
              },
              id: device.serialNumber,
            },
          }),
          getDeviceEthercatAlStateRequest({ device, timeout: 5000 }),
        ),
      ),
    ),
  );

  //
  // Get device EtherCAT AL (Application Layer) state.
  //

  handleGetDeviceEthercatAlStateRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getDeviceEthercatAlStateRequest),
      mergeMap(({ device, timeout: requestTimeout }) =>
        this.cn.client.request.getEthercatNetworkState({ deviceAddress: device.deviceAddress }, requestTimeout).pipe(
          // Intermediate progress updates report neither a state nor an error - wait for the request to
          // actually settle before recording anything.
          filter(({ state, error }) => state !== undefined || !!error),
          map(({ state, error }) => {
            if (error) {
              console.warn(`Could not read the EtherCAT AL state of device ${device.serialNumber}: ${error.message}`);
            }

            return updateDevice({
              update: {
                changes: {
                  // A device whose AL state could not be determined (e.g. Motion Master reports it as
                  // UNKNOWN_STATE or NOT_SUPPORTED) is treated the same as one confirmed to be in INIT -
                  // in both cases it cannot be told apart from a device that genuinely has no firmware.
                  ethercatAlState: state ?? MotionMasterMessage.Status.EthercatNetworkState.State.UNSPECIFIED,
                },
                id: device.serialNumber,
              },
            });
          }),
          catchError((err) => {
            console.warn(
              `Failed to read the EtherCAT AL state of device ${device.serialNumber}. Error: ${err.message}`,
            );
            return of(
              updateDevice({
                update: {
                  changes: {
                    ethercatAlState: MotionMasterMessage.Status.EthercatNetworkState.State.UNSPECIFIED,
                  },
                  id: device.serialNumber,
                },
              }),
            );
          }),
        ),
      ),
    ),
  );

  //
  // Load UI config file (ui.config.json).
  //

  /**
   * Loading UI config file after Motion Master returns a list of device parameters (info) will ensure that file operations with drive are possible.
   * If file operation was requested earlier, Motion Master could fail to retrieve the file. This is due to drive being in INIT state,
   * in which FoE is not allowed - there is no firmware in that EtherCAT state running on a device.
   */
  requestGetDeviceFileUiConfigJsonOnAddDeviceParameters$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addDeviceParameters),
      map(({ device }) =>
        getDeviceFileRequest({
          device,
          filename: 'ui.config.json',
          loadIntoStore: true,
          decodeText: true,
          suppressToastr: true,
          timeout: 5000,
          initiator: addDeviceParameters.type,
        }),
      ),
    ),
  );

  assignFeaturesToParametersOnGetDeviceFileStatusForUiConfigJson$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getDeviceFileStatus),
      filter(
        ({ deviceFile, initiator }) => deviceFile.name === 'ui.config.json' && initiator === addDeviceParameters.type,
      ),
      map(({ device }) => {
        return assignFeaturesToParameters({
          device,
        });
      }),
    ),
  );

  //
  // Load base device parameter values.
  //

  updateDeviceParameterValuesLoadedOnGetDeviceParameterValuesStatusCompleted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deviceParameterValuesLoaded),
      map(({ device }) =>
        updateDevice({
          update: {
            changes: { parameterValuesLoaded: true },
            id: device.serialNumber,
          },
        }),
      ),
    ),
  );

  fillPdoMappingParametersOnGetDeviceParameterValuesStatusCompleted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateDeviceParameters),
      filter(({ initiator }) => initiator === loadDeviceParameterValues.type),
      filter(({ device }) => {
        const version = this.deviceService.getDeviceParameterValue<string>(device, 'manufacturerSoftwareVersion');
        return semver.gt(version, '4.4.0');
      }),
      map(({ device }) => {
        const rxPdoParameters$ = this.deviceService
          .selectDeviceParameters(device, 'rxPdo')
          .pipe(map((parameters) => fillDevicePdoParametersWithMappingEntries(parameters)));

        const txPdoParameters$ = this.deviceService
          .selectDeviceParameters(device, 'txPdo')
          .pipe(map((parameters) => fillDevicePdoParametersWithMappingEntries(parameters)));

        const rxPdoParameters = getCurrentValue(rxPdoParameters$);
        const txPdoParameters = getCurrentValue(txPdoParameters$);

        const deviceParameters = rxPdoParameters.concat(txPdoParameters);

        return upsertDeviceParameters({ deviceParameters });
      }),
    ),
  );

  // resetCyclicHbFaultOnDeviceInitialized$ = createEffect(
  //   () =>
  //     this.actions$.pipe(
  //       ofType(addDeviceParameters),
  //       tap((action) => {
  //         this.deviceService.requestResetDeviceFault(action.device);
  //       }),
  //     ),
  //   { dispatch: false },
  // );

  /**
   * Start monitoring base parameters for device: Statusword, Op Mode Display, Error Code...
   * This is application-wide monitoring, stopped only when Motion Master starts to deinitialize or is not alive.
   */
  startBaseMonitoringOnGetBaseParameterValues$ = createEffect(
    () =>
      allBaseParameterValuesLoaded$(this.actions$).pipe(
        withLatestFrom(this.store$.select(selectAllDevices)),
        tap(([, devices]) => devices.forEach((device) => this.monitoringService.startBaseMonitoring(device))),
      ),
    { dispatch: false },
  );

  navigateToUrlAfterPageReload$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(addDeviceParameters),
        tap((action) => {
          const { device } = action;
          const url = sessionStorage.getItem('navigateToUrlAfterPageReload');
          if (url?.includes(device.serialNumber)) {
            this.router.navigateByUrl(url);
            sessionStorage.removeItem('navigateToUrlAfterPageReload');
          }
        }),
      ),
    { dispatch: false },
  );

  /////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  //
  // Load device SMM firmware version.
  //

  handleLoadDeviceSmmFirmwareVersion$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadDeviceSmmFirmwareVersion),
      mergeMap((action) =>
        this.cn.client.request.readSmmFirmwareVersion(action.device.serialNumber).pipe(
          map((smmFirmwareVersion) =>
            updateDevice({
              update: {
                id: action.device.serialNumber,
                changes: {
                  smmFirmwareVersion,
                },
              },
            }),
          ),
          catchError((err) => {
            this.toastr.error(
              'Failed to load device SMM firmware version. Error: ' + err.message,
              'Load Device SMM Firmware Version',
              { disableTimeOut: true },
            );
            return EMPTY;
          }),
        ),
      ),
    ),
  );

  //
  // Load device SMM serial number.
  //

  handleLoadDeviceSmmSerialNumber$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadDeviceSmmSerialNumber),
      mergeMap((action) =>
        this.cn.client.request.readSmmSerialNumber(action.device.serialNumber).pipe(
          map((smmSerialNumber) =>
            updateDevice({
              update: {
                id: action.device.serialNumber,
                changes: {
                  smmSerialNumber,
                },
              },
            }),
          ),
          catchError((err) => {
            this.toastr.error(
              'Failed to load device SMM serial number. Error: ' + err.message,
              'Load Device SMM Serial Number',
              { disableTimeOut: true },
            );
            return EMPTY;
          }),
        ),
      ),
    ),
  );

  //
  // Load device SMM temperature.
  //

  handleLoadDeviceSmmTemperature$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadDeviceSmmTemperature),
      mergeMap((action) =>
        this.cn.client.request.readSmmTemperature(action.device.serialNumber).pipe(
          map((smmTemperature) =>
            updateDevice({
              update: {
                id: action.device.serialNumber,
                changes: {
                  smmTemperature,
                },
              },
            }),
          ),
          catchError((err) => {
            this.toastr.error(
              'Failed to load device SMM temperature. Error: ' + err.message,
              'Load Device SMM Temperature',
              { disableTimeOut: true },
            );
            return EMPTY;
          }),
        ),
      ),
    ),
  );

  //
  // Load device SMM max position discrepancy.
  //

  handleLoadDeviceSmmMaxPositionDiscrepancy$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadDeviceSmmMaxPositionDiscrepancy),
      mergeMap((action) =>
        this.cn.client.request.readSmmMaxPositionDiscrepancy(action.device.serialNumber).pipe(
          map((smmMaxPositionDiscrepancy) =>
            updateDevice({
              update: {
                id: action.device.serialNumber,
                changes: {
                  smmMaxPositionDiscrepancy,
                },
              },
            }),
          ),
          catchError((err) => {
            this.toastr.error(
              'Failed to load device SMM max position discrepancy. Error: ' + err.message,
              'Load Device SMM Max Position Discrepancy',
              { disableTimeOut: true },
            );
            return EMPTY;
          }),
        ),
      ),
    ),
  );

  //
  // Get the device parameters' structure version and update the device property
  //
  updateDeviceSmmParameterStructureVersion$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateDeviceSmmParameterStructureVersion),
      mergeMap(({ deviceSerialNumber }) =>
        this.cn.client.request.resolveSmmParameterStructureVersion(deviceSerialNumber).pipe(
          map((version) =>
            updateDevice({
              update: {
                id: deviceSerialNumber,
                changes: {
                  smmParameterStructureVersion: version,
                },
              },
            }),
          ),
        ),
      ),
    ),
  );

  //
  // Reset device fault.
  //

  dispatchResetDeviceFaultStatusOnRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(resetDeviceFaultRequest),
      map((action) =>
        resetDeviceFaultStatus({
          ...action,
          deviceFaultReset: {
            deviceAddress: action.device.deviceAddress,
            begin: {},
          },
        }),
      ),
    ),
  );

  handleResetDeviceFaultRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(resetDeviceFaultRequest),
      mergeMap((action) => {
        const { device, suppressToastr } = action;

        return this.cn.client.request.resetDeviceFault({ deviceAddress: device.deviceAddress }, action.timeout).pipe(
          map((deviceFaultReset) => {
            const status = resetDeviceFaultStatus({ device, deviceFaultReset, suppressToastr });
            if (deviceFaultReset.warning?.code === MotionMasterMessage.Status.DeviceFaultReset.Warning.Code.NO_FAULT) {
              const deviceName = this.deviceService.getDeviceName(device);
              deviceFaultReset.warning.message = `No fault on ${deviceName}.`;
            }
            return status;
          }),
          catchError(() => {
            const deviceName = this.deviceService.getDeviceName(device);

            return of(
              resetDeviceFaultStatus({
                device,
                deviceFaultReset: {
                  timeout: {
                    message: `Timeout while resetting the fault on ${deviceName}.`,
                  },
                },
                suppressToastr,
              }),
            );
          }),
        );
      }),
    ),
  );

  handleResetDeviceFaultStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(resetDeviceFaultStatus),
      tap((action) =>
        this.globalService.toastrStatusMessage(action.deviceFaultReset, 'Fault Reset', action.suppressToastr),
      ),
      map((action) =>
        updateDevice({
          update: {
            changes: {
              faultResetStatus: action.deviceFaultReset,
            },
            id: action.device.serialNumber,
          },
        }),
      ),
    ),
  );

  //
  // Start Device Firmware Installation.
  //

  dispatchStartDeviceFirmwareInstallationStatusOnRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(startDeviceFirmwareInstallationRequest),
      map((action) =>
        startDeviceFirmwareInstallationStatus({
          ...action,
          deviceFirmwareInstallation: {
            deviceAddress: action.device.deviceAddress,
            begin: {},
          },
        }),
      ),
    ),
  );

  dispatchUpdateDevicePreviousFirmwareVersionOnStartDeviceFirmwareInstallationRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(startDeviceFirmwareInstallationRequest),
      withLatestFrom(this.store$),
      map(([action, state]) => {
        const { device } = action;

        let version = this.deviceService.getDeviceParameterValue(
          device.serialNumber,
          'manufacturerSoftwareVersion',
        ) as string;

        // When reinstalling or installing a new version of the calibration firmware,
        // ensure that the production firmware version in the settings object is not overwritten.
        if (isCalibrationFirmware(version)) {
          version = state.setting.previousFirmwareVersions[device.serialNumber];
        }

        return updatePreviousFirmwareVersions({
          previousFirmwareVersions: {
            ...state.setting.previousFirmwareVersions,
            [device.serialNumber]: version,
          },
        });
      }),
    ),
  );

  handleStartDeviceFirmwareInstallationRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(startDeviceFirmwareInstallationRequest),
      mergeMap((action) => {
        const { device, buffer, skipSiiInstallation, suppressToastr, firmwareVersion, initiator } = action;

        this.cn.client.stopMonitoringAll();

        return this.cn.client.request
          .installFirmwareAndCacheEsi(
            firmwareVersion,
            {
              deviceAddress: device.deviceAddress,
              firmwarePackageContent: new Uint8Array(buffer),
              skipSiiInstallation,
            },
            action.timeout,
          )
          .pipe(
            map((deviceFirmwareInstallation) =>
              startDeviceFirmwareInstallationStatus({
                device,
                deviceFirmwareInstallation,
                initiator,
                suppressToastr,
              }),
            ),
            catchError(() => {
              const deviceName = this.deviceService.getDeviceName(device);

              return of(
                startDeviceFirmwareInstallationStatus({
                  device,
                  deviceFirmwareInstallation: {
                    timeout: {
                      message: `Timeout while installing the firmware on ${deviceName}.`,
                    },
                  },
                  initiator,
                  suppressToastr,
                }),
              );
            }),
          );
      }),
    ),
  );

  handleStartDeviceFirmwareInstallationStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(startDeviceFirmwareInstallationStatus),
      tap((action) =>
        this.globalService.toastrStatusMessage(
          action.deviceFirmwareInstallation,
          'Firmware Installation',
          action.suppressToastr,
        ),
      ),
      map((action) =>
        updateDevice({
          update: {
            changes: {
              deviceFirmwareInstallation: action.deviceFirmwareInstallation,
            },
            id: action.device.serialNumber,
          },
        }),
      ),
    ),
  );

  //
  // Cogging Torque.
  //

  dispatchStartCoggingTorqueRecordingStatusOnRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(startCoggingTorqueRecordingRequest),
      map((action) =>
        startCoggingTorqueRecordingStatus({
          ...action,
          coggingTorqueRecording: {
            deviceAddress: action.device.deviceAddress,
            begin: {},
          },
        }),
      ),
    ),
  );

  handleStartCoggingTorqueRecordingRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(startCoggingTorqueRecordingRequest),
      mergeMap((action) => {
        const { device, skipAutoTuning, suppressToastr } = action;

        return this.cn.client.request
          .startCoggingTorqueRecording({ deviceAddress: device.deviceAddress, skipAutoTuning }, action.timeout)
          .pipe(
            timeout(action.timeout),
            map((coggingTorqueRecording) =>
              startCoggingTorqueRecordingStatus({ device, coggingTorqueRecording, suppressToastr }),
            ),
            catchError(() => {
              const deviceName = this.deviceService.getDeviceName(device);

              return of(
                startCoggingTorqueRecordingStatus({
                  coggingTorqueRecording: {
                    timeout: {
                      message: `Timeout while running the cogging torque recording on ${deviceName}.`,
                    },
                  },
                  device,
                  suppressToastr,
                }),
              );
            }),
          );
      }),
    ),
  );

  handleCoggingTorqueRecordingStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(startCoggingTorqueRecordingStatus),
      tap((action) =>
        this.globalService.toastrStatusMessage(
          action.coggingTorqueRecording,
          'Cogging Torque Recording',
          action.suppressToastr,
        ),
      ),
      map((action) =>
        updateDevice({
          update: {
            changes: {
              coggingTorqueRecordingStatus: action.coggingTorqueRecording,
            },
            id: action.device.serialNumber,
          },
        }),
      ),
    ),
  );

  requestGetCoggingTorqueDataOnCoggingTorqueRecordingCompleted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(startCoggingTorqueRecordingStatus),
      filter((action) => coggingTorqueRecordingCompleted(action.coggingTorqueRecording)),
      map((action) => action.device),
      map((device) =>
        getCoggingTorqueDataRequest({
          device,
          filename: 'cogging_torque.bin',
          loadIntoStore: true,
          suppressToastr: true,
          timeout: 5000,
        }),
      ),
    ),
  );

  /* @deprecated OffsetDetection */
  dispatchStartOffsetDetectionOldStatusOnRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(startOffsetDetectionOldRequest),
      map((action) =>
        updateDevice({
          update: {
            changes: {
              offsetDetectionOldStatus: { request: 'running' } as OffsetDetectionStatus,
            },
            id: action.device.serialNumber,
          },
        }),
      ),
    ),
  );

  handleStartOffsetDetectionOldRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(startOffsetDetectionOldRequest),
      mergeMap((action) => {
        const { device, suppressToastr } = action;

        return this.cn.client.request
          .startOffsetDetection({ deviceAddress: device.deviceAddress }, action.timeout)
          .pipe(
            map((offsetDetection) => startOffsetDetectionOldStatus({ device, offsetDetection, suppressToastr })),
            catchError(() => {
              const deviceName = this.deviceService.getDeviceName(device);

              return of(
                startOffsetDetectionOldStatus({
                  device,
                  offsetDetection: {
                    request: 'failed',
                    timeout: {
                      message: `Timeout while running the offset detection on ${deviceName}.`,
                    },
                  },
                  suppressToastr,
                }),
              );
            }),
          );
      }),
    ),
  );

  handleOffsetDetectionStatuses$ = createEffect(() =>
    this.actions$.pipe(
      ofType(startOffsetDetectionOldStatus),
      tap((action) =>
        this.globalService.toastrStatusMessage(action.offsetDetection, 'Offset Detection', action.suppressToastr),
      ),
      map((action) =>
        updateDevice({
          update: {
            changes: {
              offsetDetectionOldStatus: action.offsetDetection,
            },
            id: action.device.serialNumber,
          },
        }),
      ),
    ),
  );

  requestGetDeviceParameterValuesRelatedToOffsetDetectionOnOffsetDetectionCompleted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(startOffsetDetectionOldStatus),
      filter(({ offsetDetection }) => offsetDetection.request === 'succeeded'),
      map((action) => action.device),
      map((device) =>
        getDeviceParameterValuesRequest({
          device,
          parameters: [
            { index: 0x2001, subindex: 0, loadFromCache: false },
            { index: 0x2003, subindex: 5, loadFromCache: false },
            { index: 0x2009, subindex: 1, loadFromCache: false },
            { index: 0x2009, subindex: 2, loadFromCache: false },
          ],
          timeout: 5000,
        }),
      ),
    ),
  );
  /* @deprecated End OffsetDetection */

  //
  // System Identification.
  //

  dispatchStartSystemIdentificationStatusOnRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(startSystemIdentificationRequest),
      map((action) =>
        startSystemIdentificationStatus({
          ...action,
          systemIdentification: {
            deviceAddress: action.device.deviceAddress,
            begin: {},
          },
        }),
      ),
    ),
  );

  handleStartSystemIdentificationRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(startSystemIdentificationRequest),
      mergeMap((action) => {
        const { device, durationSeconds, torqueAmplitude, startFrequency, endFrequency, suppressToastr } = action;

        return this.cn.client.request
          .startSystemIdentification(
            {
              deviceAddress: device.deviceAddress,
              durationSeconds,
              torqueAmplitude,
              startFrequency,
              endFrequency,
              nextGenSysId: true,
            },
            action.timeout,
          )
          .pipe(
            map((systemIdentification) =>
              startSystemIdentificationStatus({ device, systemIdentification, suppressToastr }),
            ),
            catchError(() => {
              const deviceName = this.deviceService.getDeviceName(device);

              return of(
                startSystemIdentificationStatus({
                  device,
                  suppressToastr,
                  systemIdentification: {
                    timeout: {
                      message: `Timeout while running the system identification on ${deviceName}.`,
                    },
                  },
                }),
              );
            }),
          );
      }),
    ),
  );

  handleSystemIdentificationStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(startSystemIdentificationStatus),
      tap((action) =>
        this.globalService.toastrStatusMessage(
          action.systemIdentification,
          'System Identification',
          action.suppressToastr,
        ),
      ),
      map((action) =>
        updateDevice({
          update: {
            changes: {
              systemIdentificationStatus: action.systemIdentification,
            },
            id: action.device.serialNumber,
          },
        }),
      ),
    ),
  );

  requestGetDeviceFilePlantModelOnSystemIdentificationCompleted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(startSystemIdentificationStatus),
      filter((action) => systemIdentificationCompleted(action.systemIdentification)),
      map((action) => action.device),
      map((device) =>
        getDeviceFileRequest({
          device,
          filename: 'plant_model.csv',
          loadIntoStore: true,
          decodeText: true,
          suppressToastr: true,
          timeout: 5000,
        }),
      ),
    ),
  );

  requestGetIntegralLimitParameterValuesOnSystemIdentificationCompleted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(startSystemIdentificationStatus),
      filter((action) => systemIdentificationCompleted(action.systemIdentification)),
      map((action) => action.device),
      map((device) =>
        getDeviceParameterValuesRequest({
          device,
          parameters: [
            { index: 0x2012, subindex: 4 },
            { index: 0x2012, subindex: 8 },
            { index: 0x2011, subindex: 4 },
          ],
          timeout: 5000,
        }),
      ),
    ),
  );

  dispatchPositionAndVelocityTuningGainsAfterSystemIdentification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(startSystemIdentificationStatus),
      filter(
        ({ systemIdentification }) =>
          systemIdentificationEnded(systemIdentification) && systemIdentificationCompleted(systemIdentification),
      ),
      distinctUntilChanged(),
      tap(({ device }) => {
        this.deviceService.requestControllerAutoTuning(device, 'position');
        this.deviceService.requestControllerAutoTuning(device, 'velocity');
      }),
    ),
  );

  dispatchStartLimitedRangeSystemIdentificationStatusOnRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(startLimitedRangeSystemIdentificationRequest),
      map((action) =>
        startLimitedRangeSystemIdentificationStatus({
          ...action,
          limitedRangeSystemIdentification: {
            deviceAddress: action.device.deviceAddress,
            success: {
              code: MotionMasterMessage.Status.SystemIdentification.Success.Code.STARTED,
            },
          },
        }),
      ),
    ),
  );

  handleStartLimitedRangeSystemIdentificationRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(startLimitedRangeSystemIdentificationRequest),
      mergeMap((action) => {
        const lrsiWorker = this.cn.getLrsiWorker();

        const options = {
          rangeLimit: action.rangeLimit,
          rangeLimitMin: 1000,
          modesOfOperation: ModesOfOperation.CYCLIC_SYNC_POSITION_MODE,
          targetAmplitude: action.torqueAmplitude,
          startFrequency: action.startFrequency,
          targetFrequency: action.endFrequency,
          transitionTime: action.durationSeconds,
          hrdStreamingDuration: action.hrdStreamingDuration,
          signalType: SystemIdentificationOsCommandSignalType.LINEAR_WITH_CONSTANT_AMPLITUDE,
          startProcedure: SystemIdentificationOsCommandStartProcedure.WAIT_FOR_HRD_STREAMING_TO_START,
        };

        lrsiWorker.postMessage({
          action: 'start',
          payload: { deviceRef: action.device.deviceAddress, options },
        });

        return fromEvent<MessageEvent>(lrsiWorker, 'message').pipe(
          map((event) => {
            switch (event.data.type) {
              case 'COMPLETED':
                return startLimitedRangeSystemIdentificationStatus({
                  ...action,
                  limitedRangeSystemIdentification: {
                    deviceAddress: action.device.deviceAddress,
                    success: {
                      code: MotionMasterMessage.Status.SystemIdentification.Success.Code.DONE,
                      message: 'Limited range system identification successfully finished!',
                    },
                  },
                });

              case 'ERROR':
                return startLimitedRangeSystemIdentificationStatus({
                  ...action,
                  limitedRangeSystemIdentification: {
                    deviceAddress: action.device.deviceAddress,
                    error: {
                      message: event.data.payload.message,
                    },
                  },
                });

              case 'NOTIFICATION':
                return startLimitedRangeSystemIdentificationNotification({
                  device: action.device,
                  limitedRangeSystemIdentification: event.data.payload,
                  suppressToastr: action.suppressToastr,
                  message: event.data.payload.message,
                });
            }
          }),
        );
      }),
    ),
  );

  handleLimitedRangeSystemIdentificationStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(startLimitedRangeSystemIdentificationStatus),
      tap((action) =>
        this.globalService.toastrStatusMessage(
          action.limitedRangeSystemIdentification,
          'Limited Range System Identification',
          action.suppressToastr,
        ),
      ),
      map((action) =>
        updateDevice({
          update: {
            changes: {
              limitedRangeSystemIdentificationStatus: action.limitedRangeSystemIdentification,
            },
            id: action.device.serialNumber,
          },
        }),
      ),
    ),
  );

  requestGetDeviceFilePlantModelOnLimitedRangeSystemIdentificationCompleted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(startLimitedRangeSystemIdentificationStatus),
      filter((action) => limitedRangeSystemIdentificationCompleted(action.limitedRangeSystemIdentification)),
      map((action) => action.device),
      map((device) =>
        getDeviceFileRequest({
          device,
          filename: 'plant_model.csv',
          loadIntoStore: true,
          decodeText: true,
          suppressToastr: true,
          timeout: 5000,
        }),
      ),
    ),
  );

  requestGetIntegralLimitParameterValuesOnLimitedRangeSystemIdentificationCompleted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(startLimitedRangeSystemIdentificationStatus),
      filter((action) => limitedRangeSystemIdentificationCompleted(action.limitedRangeSystemIdentification)),
      map((action) => action.device),
      map((device) =>
        getDeviceParameterValuesRequest({
          device,
          parameters: [
            { index: 0x2012, subindex: 4 },
            { index: 0x2012, subindex: 8 },
            { index: 0x2011, subindex: 4 },
          ],
          timeout: 5000,
        }),
      ),
    ),
  );

  dispatchPositionAndVelocityTuningGainsAfterLimitedRangeSystemIdentification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(startLimitedRangeSystemIdentificationStatus),
      filter(
        ({ limitedRangeSystemIdentification }) =>
          limitedRangeSystemIdentificationEnded(limitedRangeSystemIdentification) &&
          limitedRangeSystemIdentificationCompleted(limitedRangeSystemIdentification),
      ),
      distinctUntilChanged(),
      tap(({ device }) => {
        this.deviceService.requestControllerAutoTuning(device, 'position');
        this.deviceService.requestControllerAutoTuning(device, 'velocity');
      }),
    ),
  );

  //
  // Compute Position Auto-Tuning Gains.
  //

  dispatchComputePositionAutoTuningGainsStatusOnRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(computePositionAutoTuningGainsRequest),
      map((action) =>
        computePositionAutoTuningGainsStatus({
          ...action,
          autoTuning: {
            deviceAddress: action.device.deviceAddress,
            begin: {},
          },
        }),
      ),
    ),
  );

  handleComputePositionAutoTuningGainsRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(computePositionAutoTuningGainsRequest),
      mergeMap((action) => {
        const { device, positionParameters, suppressToastr } = action;

        return this.cn.client.request
          .computeAutoTuningGains({ deviceAddress: device.deviceAddress, positionParameters }, action.timeout)
          .pipe(
            map((autoTuning) => computePositionAutoTuningGainsStatus({ autoTuning, device, suppressToastr })),
            catchError(() => {
              const deviceName = this.deviceService.getDeviceName(device);

              return of(
                computePositionAutoTuningGainsStatus({
                  autoTuning: {
                    timeout: {
                      message: `Timeout while computing the position auto-tuning gains on ${deviceName}.`,
                    },
                  },
                  device,
                  suppressToastr,
                }),
              );
            }),
          );
      }),
    ),
  );

  handleComputePositionAutoTuningGainsStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(computePositionAutoTuningGainsStatus),
      tap((action) =>
        this.globalService.toastrStatusMessage(action.autoTuning, 'Position Auto-Tuning', action.suppressToastr),
      ),
      map((action) =>
        updateDevice({
          update: {
            changes: {
              positionAutoTuningStatus: action.autoTuning,
            },
            id: action.device.serialNumber,
          },
        }),
      ),
    ),
  );

  requestGetDeviceParameterValuesRelatedToPositionAutoTuningOnComputePositionAutoTuningGainsCompleted$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(computePositionAutoTuningGainsStatus, startFullAutoTuningStatus),
        filter((action) => {
          if (action.type === computePositionAutoTuningGainsStatus.type) {
            return positionAutoTuningCompleted(action.autoTuning);
          } else if (action.type === startFullAutoTuningStatus.type) {
            return positionFullAutoTuningCompleted(action.fullAutoTuning);
          }
        }),
        map((action) => action.device),
        map((device) =>
          getDeviceParameterValuesRequest({
            device,
            parameters: [
              { index: 0x2012, subindex: 1 },
              { index: 0x2012, subindex: 2 },
              { index: 0x2012, subindex: 3 },
              { index: 0x2012, subindex: 4 },
              { index: 0x2012, subindex: 5 },
              { index: 0x2012, subindex: 6 },
              { index: 0x2012, subindex: 7 },
              { index: 0x2012, subindex: 8 },
            ],
            timeout: 10000,
          }),
        ),
      ),
  );

  //
  // Compute Velocity Auto-Tuning Gains.
  //

  dispatchComputeVelocityAutoTuningGainsStatusOnRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(computeVelocityAutoTuningGainsRequest),
      map((action) =>
        computeVelocityAutoTuningGainsStatus({
          ...action,
          autoTuning: {
            deviceAddress: action.device.deviceAddress,
            begin: {},
          },
        }),
      ),
    ),
  );

  handleComputeVelocityAutoTuningGainsRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(computeVelocityAutoTuningGainsRequest),
      mergeMap((action) => {
        const { device, velocityParameters, suppressToastr } = action;

        return this.cn.client.request
          .computeAutoTuningGains({ deviceAddress: device.deviceAddress, velocityParameters }, action.timeout)
          .pipe(
            map((autoTuning) => computeVelocityAutoTuningGainsStatus({ autoTuning, device, suppressToastr })),
            catchError(() => {
              const deviceName = this.deviceService.getDeviceName(device);

              return of(
                computeVelocityAutoTuningGainsStatus({
                  autoTuning: {
                    timeout: {
                      message: `Timeout while computing the velocity auto-tuning gains on ${deviceName}.`,
                    },
                  },
                  device,
                  suppressToastr,
                }),
              );
            }),
          );
      }),
    ),
  );

  handleComputeVelocityAutoTuningGainsStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(computeVelocityAutoTuningGainsStatus),
      tap((action) =>
        this.globalService.toastrStatusMessage(action.autoTuning, 'Velocity Auto-Tuning', action.suppressToastr),
      ),
      map((action) =>
        updateDevice({
          update: {
            changes: {
              velocityAutoTuningStatus: action.autoTuning,
            },
            id: action.device.serialNumber,
          },
        }),
      ),
    ),
  );

  requestGetDeviceParameterValuesRelatedToVelocityAutoTuningOnComputeVelocityAutoTuningGainsCompleted$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(computeVelocityAutoTuningGainsStatus, startFullAutoTuningStatus),
        filter((action) => {
          if (action.type === computeVelocityAutoTuningGainsStatus.type) {
            return velocityAutoTuningCompleted(action.autoTuning);
          } else if (action.type === startFullAutoTuningStatus.type) {
            return velocityFullAutoTuningCompleted(action.fullAutoTuning);
          }
        }),
        map((action) => action.device),
        map((device) =>
          getDeviceParameterValuesRequest({
            device,
            parameters: [
              { index: 0x2011, subindex: 1 },
              { index: 0x2011, subindex: 2 },
              { index: 0x2011, subindex: 3 },
              { index: 0x2011, subindex: 4 },
            ],
            timeout: 5000,
          }),
        ),
      ),
  );

  //
  // Start Full Auto-Tuning.
  //

  dispatchStartFullAutoTuningStatusOnRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(startFullAutoTuningRequest),
      map((action) =>
        startFullAutoTuningStatus({
          ...action,
          fullAutoTuning: {
            deviceAddress: action.device.deviceAddress,
            begin: {},
          },
        }),
      ),
    ),
  );

  handleStartFullAutoTuningRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(startFullAutoTuningRequest),
      mergeMap((action) => {
        const { device, suppressToastr, tuningType, controllerType } = action;

        return this.cn.client.request
          .startFullAutoTuning(
            { deviceAddress: device.deviceAddress, controllerType, type: tuningType },
            action.timeout,
          )
          .pipe(
            map((fullAutoTuning) => startFullAutoTuningStatus({ fullAutoTuning, device, suppressToastr })),
            catchError(() => {
              const deviceName = this.deviceService.getDeviceName(device);

              return of(
                startFullAutoTuningStatus({
                  device,
                  fullAutoTuning: {
                    timeout: {
                      message: `Timeout while running the full auto-tuning on ${deviceName}.`,
                    },
                  },
                  suppressToastr,
                }),
              );
            }),
          );
      }),
    ),
  );

  handleStartFullAutoTuningStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(startFullAutoTuningStatus),
      tap((action) =>
        this.globalService.toastrStatusMessage(action.fullAutoTuning, 'Start Full Auto-Tuning', action.suppressToastr),
      ),
      map((action) =>
        updateDevice({
          update: {
            changes: {
              fullAutoTuningStatus: action.fullAutoTuning,
            },
            id: action.device.serialNumber,
          },
        }),
      ),
    ),
  );

  updatePositionTuningControllerParametersOnStartFullAutoTuningCompleted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(startFullAutoTuningStatus),
      filter((action) => positionFullAutoTuningCompleted(action.fullAutoTuning)),
      map((action) => {
        const {
          device,
          fullAutoTuning: { dampingRatio, settlingTime },
        } = action;

        const dampingId = `positionDamping.position.${device.serialNumber}`;
        const settlingTimeId = `settlingTime.position.${device.serialNumber}`;

        return updateTuningControllerParameters({
          updates: [
            {
              changes: {
                floatValue: dampingRatio,
                value: dampingRatio,
              },
              id: dampingId,
            },
            {
              changes: {
                uintValue: 1000 * settlingTime,
                value: 1000 * settlingTime,
              },
              id: settlingTimeId,
            },
          ],
        });
      }),
    ),
  );

  updateVelocityTuningControllerParametersOnStartFullAutoTuningCompleted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(startFullAutoTuningStatus),
      filter((action) => velocityFullAutoTuningCompleted(action.fullAutoTuning)),
      map((action) => {
        const {
          device,
          fullAutoTuning: { dampingRatio, bandwidth },
        } = action;

        const dampingId = `velocityDamping.velocity.${device.serialNumber}`;
        const bandwidthId = `velocityLoopBandwidth.velocity.${device.serialNumber}`;

        return updateTuningControllerParameters({
          updates: [
            {
              changes: {
                floatValue: dampingRatio,
                value: dampingRatio,
              },
              id: dampingId,
            },
            {
              changes: {
                uintValue: bandwidth,
                value: bandwidth,
              },
              id: bandwidthId,
            },
          ],
        });
      }),
    ),
  );

  //
  // Stop Full Auto-Tuning.
  //

  dispatchStopFullAutoTuningStatusOnRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(stopFullAutoTuningRequest),
      map((action) =>
        stopFullAutoTuningStatus({
          ...action,
          fullAutoTuning: {
            deviceAddress: action.device.deviceAddress,
            begin: {},
          },
        }),
      ),
    ),
  );

  handleStopFullAutoTuningRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(stopFullAutoTuningRequest),
      mergeMap((action) => {
        const { device, suppressToastr } = action;

        return this.cn.client.request.stopFullAutoTuning({ deviceAddress: device.deviceAddress }, action.timeout).pipe(
          map((fullAutoTuning) => stopFullAutoTuningStatus({ fullAutoTuning, device, suppressToastr })),
          catchError(() => {
            const deviceName = this.deviceService.getDeviceName(device);

            return of(
              stopFullAutoTuningStatus({
                device,
                fullAutoTuning: {
                  timeout: {
                    message: `Timeout while stopping the full auto-tuning on ${deviceName}.`,
                  },
                },
                suppressToastr,
              }),
            );
          }),
        );
      }),
    ),
  );

  handleStopFullAutoTuningStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(stopFullAutoTuningStatus),
      tap((action) =>
        this.globalService.toastrStatusMessage(action.fullAutoTuning, 'Stop Full Auto-Tuning', action.suppressToastr),
      ),
      map((action) =>
        updateDevice({
          update: {
            changes: {
              fullAutoTuningStatus: action.fullAutoTuning,
            },
            id: action.device.serialNumber,
          },
        }),
      ),
    ),
  );

  //
  // Enable Motion Controller.
  //

  dispatchEnableMotionControllerStatusOnRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(enableMotionControllerRequest),
      map((action) =>
        enableMotionControllerStatus({
          ...action,
          motionController: {
            deviceAddress: action.device.deviceAddress,
            begin: {},
          },
        }),
      ),
    ),
  );

  handleEnableMotionControllerRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(enableMotionControllerRequest),
      mergeMap((action) => {
        const { device, controllerType, suppressToastr } = action;

        return this.cn.client.request
          .enableMotionController(
            { deviceAddress: device.deviceAddress, controllerType, filter: action.filter },
            action.timeout,
          )
          .pipe(
            map((motionController) => enableMotionControllerStatus({ device, motionController, suppressToastr })),
            catchError(() => {
              const deviceName = this.deviceService.getDeviceName(device);

              return of(
                enableMotionControllerStatus({
                  device,
                  motionController: {
                    timeout: {
                      message: `Timeout while enabling the motion controller on ${deviceName}.`,
                    },
                  },
                  suppressToastr,
                }),
              );
            }),
          );
      }),
    ),
  );

  handleEnableMotionControllerStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(enableMotionControllerStatus),
      tap((action) =>
        this.globalService.toastrStatusMessage(
          action.motionController,
          'Enable Motion Controller',
          action.suppressToastr,
        ),
      ),
      map((action) =>
        updateDevice({
          update: {
            changes: {
              enableMotionControllerStatus: action.motionController,
            },
            id: action.device.serialNumber,
          },
        }),
      ),
    ),
  );

  //
  // Disable Motion Controller.
  //

  dispatchDisableMotionControllerStatusOnRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(disableMotionControllerRequest),
      map((action) =>
        disableMotionControllerStatus({
          ...action,
          motionController: {
            deviceAddress: action.device.deviceAddress,
            begin: {},
          },
        }),
      ),
    ),
  );

  handleDisableMotionControllerRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(disableMotionControllerRequest),
      mergeMap((action) => {
        const { device, suppressToastr } = action;

        return this.cn.client.request
          .disableMotionController({ deviceAddress: device.deviceAddress }, action.timeout)
          .pipe(
            map((motionController) => disableMotionControllerStatus({ device, motionController, suppressToastr })),
            catchError(() => {
              const deviceName = this.deviceService.getDeviceName(device);

              return of(
                disableMotionControllerStatus({
                  device,
                  motionController: {
                    timeout: {
                      message: `Timeout while disabling the motion controller on ${deviceName}.`,
                    },
                  },
                  suppressToastr,
                }),
              );
            }),
          );
      }),
    ),
  );

  handleDisableMotionControllerStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(disableMotionControllerStatus),
      tap((action) =>
        this.globalService.toastrStatusMessage(
          action.motionController,
          'Disable Motion Controller',
          action.suppressToastr,
        ),
      ),
      map((action) =>
        updateDevice({
          update: {
            changes: {
              disableMotionControllerStatus: action.motionController,
            },
            id: action.device.serialNumber,
          },
        }),
      ),
    ),
  );

  //
  // Start Signal Generator.
  //

  dispatchSignalGeneratorStatusOnStartRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(startSignalGeneratorRequest),
      map((action) =>
        startSignalGeneratorStatus({
          ...action,
          signalGenerator: {
            deviceAddress: action.device.deviceAddress,
            begin: {},
          },
        }),
      ),
    ),
  );

  handleStartSignalGeneratorRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(startSignalGeneratorRequest),
      mergeMap((action) => {
        const { device, suppressToastr, signalGeneratorId, values } = action;

        return this.cn.client.request.startSignalGenerator({ deviceAddress: device.deviceAddress }, 1_814_400_000).pipe(
          // 21 days for the repeating profiles
          map((signalGenerator) =>
            startSignalGeneratorStatus({ device, signalGeneratorId, values, signalGenerator, suppressToastr }),
          ),
          catchError(() => {
            const deviceName = this.deviceService.getDeviceName(device);

            return of(
              startSignalGeneratorStatus({
                device,
                signalGenerator: {
                  timeout: {
                    message: `Timeout while starting the signal generator on ${deviceName}.`,
                  },
                },
                signalGeneratorId: action.signalGeneratorId,
                suppressToastr,
                values: action.values,
              }),
            );
          }),
        );
      }),
    ),
  );

  handleStartSignalGeneratorStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(startSignalGeneratorStatus),
      tap((action) =>
        this.globalService.toastrStatusMessage(action.signalGenerator, 'Start Signal Generator', action.suppressToastr),
      ),
      map((action) =>
        updateDevice({
          update: {
            changes: {
              startSignalGeneratorStatus: action.signalGenerator,
            },
            id: action.device.serialNumber,
          },
        }),
      ),
    ),
  );

  //
  // Stop Signal Generator.
  //

  dispatchSignalGeneratorStatusOnStopRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(stopSignalGeneratorRequest),
      map((action) =>
        stopSignalGeneratorStatus({
          ...action,
          signalGenerator: {
            deviceAddress: action.device.deviceAddress,
            begin: {},
          },
        }),
      ),
    ),
  );

  handleStopSignalGeneratorRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(stopSignalGeneratorRequest),
      mergeMap((action) => {
        const { device, suppressToastr } = action;

        return this.cn.client.request.stopSignalGenerator({ deviceAddress: device.deviceAddress }, 5000).pipe(
          map((signalGenerator) => stopSignalGeneratorStatus({ device, signalGenerator, suppressToastr })),
          catchError(() => {
            const deviceName = this.deviceService.getDeviceName(device);

            return of(
              stopSignalGeneratorStatus({
                device,
                signalGenerator: {
                  timeout: {
                    message: `Timeout while stopping the signal generator on ${deviceName}.`,
                  },
                },
                suppressToastr,
              }),
            );
          }),
        );
      }),
    ),
  );

  handleStopSignalGeneratorStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(stopSignalGeneratorStatus),
      tap((action) =>
        this.globalService.toastrStatusMessage(action.signalGenerator, 'Stop Signal Generator', action.suppressToastr),
      ),
      map((action) =>
        updateDevice({
          update: {
            changes: {
              stopSignalGeneratorStatus: action.signalGenerator,
            },
            id: action.device.serialNumber,
          },
        }),
      ),
    ),
  );

  //
  // Save device configuration.
  //

  dispatchSaveConfigurationToDeviceStatusOnRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(saveConfigurationToDeviceRequest),
      map((action) => {
        const { device } = action;
        const deviceName = this.deviceService.getDeviceName(device);

        return saveConfigurationToDeviceStatus({
          ...action,
          procedureStatus: {
            begin: {
              message: `Saving changes to the ${deviceName} configuration file.`,
            },
          },
        });
      }),
    ),
  );

  handleSaveConfigurationToDeviceRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(saveConfigurationToDeviceRequest),
      mergeMap((action) => {
        const { device, suppressToastr } = action;
        const deviceName = this.deviceService.getDeviceName(device);
        const disabled = getCurrentValue(this.deviceService.selectDeviceSaveConfigurationButtonDisabled(device));

        const obs1$ = of(
          saveConfigurationToDeviceStatus({
            device,
            procedureStatus: {
              error: { message: `Saving changes to the ${deviceName} configuration file is disabled!` },
            },
            suppressToastr,
          }),
        );

        const obs2$ = defer(() =>
          this.cn.client.request
            .setDeviceParameterValues(
              { deviceAddress: device.deviceAddress, parameterValues: evasParameterValues },
              action.timeout,
            )
            .pipe(
              tap((deviceParameterValues) => {
                if (deviceParameterValuesHaveErrors(deviceParameterValues)) {
                  throw new Error('Failed to set the value of save all parameters.');
                }
              }),
              delay(2500), // delay the first call to check the save configuration to device
              mergeMap(() =>
                this.checkCommandExecutedOnSlave(device, evasParameterValues).pipe(
                  retryWithDelay(3000, 5),
                  map(() =>
                    saveConfigurationToDeviceStatus({
                      device,
                      procedureStatus: {
                        success: {
                          code: ProcedureStatusSuccessCode.DONE,
                          message: `The changes have been saved to the ${deviceName} configuration file.`,
                        },
                      },
                      suppressToastr,
                    }),
                  ),
                ),
              ),
              timeout(action.timeout), // total timeout for this procedure
              catchError((error) => {
                let message = `Failed to save the configuration to device ${deviceName}.`;

                if (error) {
                  message += ` Error: ${error.message}`;
                }

                return of(
                  saveConfigurationToDeviceStatus({
                    device,
                    procedureStatus: { error: { message } },
                    suppressToastr,
                  }),
                );
              }),
            ),
        );

        return iif(() => disabled, obs1$, obs2$);
      }),
    ),
  );

  handleSaveConfigurationToDeviceStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(saveConfigurationToDeviceStatus),
      tap((action) =>
        this.globalService.toastrStatusMessage(action.procedureStatus, 'Save Changes', action.suppressToastr),
      ),
      map((action) =>
        updateDevice({
          update: {
            changes: {
              saveConfigurationToDeviceStatus: action.procedureStatus,
            },
            id: action.device.serialNumber,
          },
        }),
      ),
    ),
  );

  loadDeviceConfigurationFileOnSaveConfigurationToDeviceStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(saveConfigurationToDeviceStatus),
      filter((status) => !!status.procedureStatus.success),
      map((status) =>
        getDeviceFileRequest({
          device: status.device,
          filename: 'config.csv',
          decodeText: true,
          loadIntoStore: true,
          suppressToastr: true,
          timeout: 5000,
        }),
      ),
    ),
  );

  //
  // Load configuration from file.
  //

  dispatchLoadDeviceConfigurationFromFileStatusOnRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadDeviceConfigurationFromFileRequest),
      map((action) => {
        const deviceName = this.deviceService.getDeviceName(action.device);

        return loadDeviceConfigurationFromFileStatus({
          ...action,
          procedureStatus: {
            begin: {
              message: `Loading the configuration from file on ${deviceName}.`,
            },
          },
        });
      }),
    ),
  );

  handleLoadDeviceConfigurationFromFileRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadDeviceConfigurationFromFileRequest),
      exhaustMap((action) => {
        const deviceName = this.deviceService.getDeviceName(action.device);

        return this.cn.client.request.loadConfig(action.device.serialNumber, action.content, action.strategy).pipe(
          map(() => getConfigurationFileParametersRequest({ device: action.device })),
          catchError((error) =>
            of(
              loadDeviceConfigurationFromFileStatus({
                device: action.device,
                procedureStatus: {
                  error: {
                    message: `Failed to load the configuration from file on ${deviceName}. ${error.message}`,
                  },
                },
              }),
            ),
          ),
        );
      }),
    ),
  );

  handleGetConfigParametersRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getConfigurationFileParametersRequest),
      mergeMap((action) =>
        this.cn.client.request
          .getConfigFileParameters(action.device.serialNumber)
          .pipe(
            map((configParameters) =>
              updateDeviceParametersFromConfig({ parameters: configParameters, device: action.device }),
            ),
          ),
      ),
    ),
  );

  handleUpdateDeviceParametersRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateDeviceParametersFromConfig),
      map((action) => {
        const deviceParameters = action.parameters.map((deviceParameter) => {
          const id = makeDeviceParameterId(action.device.serialNumber, deviceParameter);
          return { id, ...deviceParameter };
        }) as DeviceParameter[];

        return upsertDeviceParameters({ deviceParameters });
      }),
    ),
  );

  handleUpdateDeviceParametersStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateDeviceParametersFromConfig),
      map((action) => {
        const deviceName = this.deviceService.getDeviceName(action.device);

        return loadDeviceConfigurationFromFileStatus({
          device: action.device,
          procedureStatus: {
            success: {
              code: ProcedureStatusSuccessCode.DONE,
              message: `Load configuration from file on ${deviceName} is done.`,
            },
          },
        });
      }),
    ),
  );

  handleLoadDeviceConfigurationFromFileStatusSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadDeviceConfigurationFromFileStatus),
      filter((action) => procedureStatusCompleted(action.procedureStatus)),
      tap((action) => {
        this.globalService.toastrStatusMessage(
          action.procedureStatus,
          'Load Configuration From File',
          this.deviceService.loadConfigurationFromFileSuppressToastr,
        );
      }),
      map((action) => {
        return updateDevice({
          update: {
            changes: {
              loadConfigurationFromFileStatus: action.procedureStatus,
            },
            id: action.device.serialNumber,
          },
        });
      }),
    ),
  );

  handleLoadDeviceConfigurationFromFileStatusError$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadDeviceConfigurationFromFileStatus),
      filter((action) => !procedureStatusCompleted(action.procedureStatus)),
      tap((action) =>
        this.globalService.toastrStatusMessage(
          action.procedureStatus,
          'Load Configuration From File',
          this.deviceService.loadConfigurationFromFileSuppressToastr,
        ),
      ),
      map((action) => {
        return updateDevice({
          update: {
            changes: {
              loadConfigurationFromFileStatus: action.procedureStatus,
            },
            id: action.device.serialNumber,
          },
        });
      }),
    ),
  );

  //
  // Factory Reset
  //

  dispatchFactoryResetStatusOnRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(factoryResetRequest),
      map((action) => {
        const { device } = action;
        const deviceName = this.deviceService.getDeviceName(device);

        return factoryResetStatus({
          ...action,
          procedureStatus: {
            begin: {
              code: ProcedureStatusSuccessCode.STARTED,
              message: `Started the factory reset of ${deviceName}.`,
            },
          },
        });
      }),
    ),
  );

  handleFactoryResetStatusOnRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(factoryResetRequest),
      withLatestFrom(this.store$),
      concatMap(async ([action, state]) => {
        const { device, installEmptyFirmware, suppressToastr } = action;

        try {
          const factoryResetRequestStatus = await lastValueFrom(
            this.cn.client.request.factoryReset(
              device.deviceAddress,
              factoryResetDefaultFilesToKeep,
              installEmptyFirmware,
              true,
            ),
          );
          const parameters = selectAllDeviceParameters(state).filter(
            (deviceParameter) => deviceParameter.deviceSerialNumber === device.serialNumber,
          );
          this.deviceService.dispatchRequestDeviceParameterValues(device, parameters);
          if (factoryResetRequestStatus.request === 'succeeded') {
            return factoryResetStatus({
              device,
              procedureStatus: {
                success: {
                  code: ProcedureStatusSuccessCode.DONE,
                  message: `The factory reset of device ${device.serialNumber} is complete`,
                },
              },
              suppressToastr,
            });
          } else if (factoryResetRequestStatus.request === 'failed') {
            throw new Error(`The factory reset of the device ${device.serialNumber} has failed.`);
          }
        } catch (err) {
          return factoryResetStatus({
            device,
            procedureStatus: {
              error: {
                code: err.code,
                message: err.message,
              },
            },
            suppressToastr,
          });
        }
      }),
    ),
  );

  handleFactoryResetStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(factoryResetStatus),
      tap((action) =>
        this.globalService.toastrStatusMessage(action.procedureStatus, 'Factory Reset', action.suppressToastr),
      ),
      map((action) =>
        updateDevice({
          update: {
            changes: {
              factoryResetStatus: action.procedureStatus,
            },
            id: action.device.serialNumber,
          },
        }),
      ),
    ),
  );

  //
  // Circulo Encoder Narrow Angle Calibration Procedure
  //

  dispatchStartCirculoEncoderNarrowAngleCalibrationProcedureStatusOnRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(startCirculoEncoderNarrowAngleCalibrationProcedureRequest),
      map((action) =>
        startCirculoEncoderNarrowAngleCalibrationProcedureStatus({
          ...action,
          circuloEncoderNarrowAngleCalibrationProcedure: {
            deviceAddress: action.device.deviceAddress,
            begin: {},
          },
        }),
      ),
    ),
  );

  handleStartCirculoEncoderNarrowAngleCalibrationProcedureRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(startCirculoEncoderNarrowAngleCalibrationProcedureRequest),
      mergeMap((action) => {
        const {
          device,
          encoderOrdinal,
          activateHealthMonitoring,
          measurementOnly,
          velocityBaseValueRpm,
          suppressToastr,
        } = action;

        return this.cn.client.request
          .startCirculoEncoderNarrowAngleCalibrationProcedure(
            {
              deviceAddress: device.deviceAddress,
              encoderOrdinal,
              activateHealthMonitoring,
              measurementOnly,
              // When unset, Motion Master picks the base velocity for the encoder (e.g. 90 rpm Circulo 7, 45 rpm Circulo 9).
              ...(velocityBaseValueRpm != null && { velocityBaseValueRpm }),
            },
            action.timeout,
          )
          .pipe(
            map((circuloEncoderNarrowAngleCalibrationProcedure) =>
              startCirculoEncoderNarrowAngleCalibrationProcedureStatus({
                circuloEncoderNarrowAngleCalibrationProcedure,
                device,
                suppressToastr,
                measurementOnly,
              }),
            ),
            catchError(() => {
              const deviceName = this.deviceService.getDeviceName(device);

              return of(
                startCirculoEncoderNarrowAngleCalibrationProcedureStatus({
                  circuloEncoderNarrowAngleCalibrationProcedure: {
                    timeout: {
                      message: `Timeout while running the circulo encoder narrow angle calibration procedure on ${deviceName}.`,
                    },
                  },
                  device,
                  suppressToastr,
                }),
              );
            }),
          );
      }),
    ),
  );

  handleStartCirculoEncoderNarrowAngleCalibrationProcedureStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(startCirculoEncoderNarrowAngleCalibrationProcedureStatus),
      tap((action) => {
        if (
          action.measurementOnly &&
          action.circuloEncoderNarrowAngleCalibrationProcedure.success?.code ===
            MotionMasterMessage.Status.CirculoEncoderNarrowAngleCalibrationProcedure.Success.Code.DONE
        ) {
          this.toastr.success('Validation complete.', 'Circulo Encoder Narrow Angle Calibration');
        } else {
          this.globalService.toastrStatusMessage(
            action.circuloEncoderNarrowAngleCalibrationProcedure,
            'Circulo Encoder Narrow Angle Calibration',
            action.suppressToastr,
          );
        }
      }),
      map((action) => {
        const status = action.circuloEncoderNarrowAngleCalibrationProcedure;
        const changes: Partial<Device> = {
          circuloEncoderNarrowAngleCalibrationProcedureStatus: status,
        };

        if (circuloEncoderNarrowAngleCalibrationProcedureCompleted(status)) {
          changes.circuloEncoderConfigurationBinWriteRunning = !action.measurementOnly;
        } else if (circuloEncoderNarrowAngleCalibrationProcedureEnded(status)) {
          changes.circuloEncoderConfigurationBinWriteRunning = false;
        }

        return updateDevice({
          update: {
            changes,
            id: action.device.serialNumber,
          },
        });
      }),
    ),
  );

  setDefaultFeedbackFilterCutoffFrequenciesForCirculoOnCalibrationProcedureCompleted$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(startCirculoEncoderNarrowAngleCalibrationProcedureStatus),
        filter(
          (action) =>
            !action.measurementOnly &&
            circuloEncoderNarrowAngleCalibrationProcedureCompleted(
              action.circuloEncoderNarrowAngleCalibrationProcedure,
            ),
        ),
        tap(({ device }) => {
          this.deviceService.setDefaultFeedbackFilterCutoffFrequenciesForCirculo(device);
        }),
      ),
    { dispatch: false },
  );

  //
  // Reload Device State Machine.
  //

  dispatchReloadDeviceStateMachineStatusOnRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(reloadDeviceStateMachineRequest),
      map((action) =>
        reloadDeviceStateMachineStatus({
          ...action,
          deviceStateMachineStatus: {
            begin: {
              message: 'Reloading the device state machine: SWITCH ON DISABLED to READY TO SWITCH ON.',
            },
          },
        }),
      ),
    ),
  );

  handleReloadDeviceStateMachineRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(reloadDeviceStateMachineRequest),
      mergeMap((action) => {
        const { device, suppressToastr } = action;

        return this.cn.client.request
          .setDeviceCia402State(
            {
              deviceAddress: device.deviceAddress,
              state: MotionMasterMessage.Status.DeviceCiA402State.State.SWITCH_ON_DISABLED,
            },
            5000,
          )
          .pipe(
            map((deviceCia402State) => {
              if (deviceCia402StateCompleted(deviceCia402State)) {
                return deviceCia402State;
              } else {
                throw new Error(deviceCia402State.error?.message);
              }
            }),
            concatMap(() =>
              this.cn.client.request
                .setDeviceCia402State(
                  {
                    deviceAddress: device.deviceAddress,
                    state: MotionMasterMessage.Status.DeviceCiA402State.State.READY_SWITCH_ON,
                  },
                  5000,
                )
                .pipe(
                  takeWhile(deviceCia402StateRunning, true),
                  map((deviceCia402State) => {
                    if (deviceCia402StateCompleted(deviceCia402State)) {
                      return reloadDeviceStateMachineStatus({
                        device,
                        deviceStateMachineStatus: deviceCia402State,
                        suppressToastr,
                      });
                    } else {
                      throw new Error(deviceCia402State.error?.message);
                    }
                  }),
                ),
            ),
            timeout(action.timeout),
            catchError((error: Error) => {
              const deviceName = this.deviceService.getDeviceName(device);

              return of(
                reloadDeviceStateMachineStatus({
                  device,
                  deviceStateMachineStatus: {
                    timeout: {
                      message: `Failed to reload the state machine on ${deviceName}. Error: ${error.message}`,
                    },
                  },
                  suppressToastr,
                }),
              );
            }),
          );
      }),
    ),
  );

  handleReloadDeviceStateMachineStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(reloadDeviceStateMachineStatus),
      tap((action) =>
        this.globalService.toastrStatusMessage(
          action.deviceStateMachineStatus,
          'Reload Device State Machine',
          action.suppressToastr,
        ),
      ),
      map((action) =>
        updateDevice({
          update: {
            changes: {
              deviceStateMachineStatus: action.deviceStateMachineStatus,
            },
            id: action.device.serialNumber,
          },
        }),
      ),
    ),
  );

  //
  // Open Loop Field Control
  //

  dispatchOpenLoopSignalGeneratorStatusOnStartRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(startOpenLoopFieldControlRequest),
      map((action) =>
        startOpenLoopFieldControlStatus({
          ...action,
          openLoopFieldControl: {
            begin: {
              message: 'Starting the open loop field control',
            },
          },
        }),
      ),
    ),
  );

  handleStartOpenLoopFieldControlRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(startOpenLoopFieldControlRequest),
      mergeMap((action) => {
        const { device, properties, suppressToastr } = action;

        return this.cn.client.request.startOpenLoopFieldControl(properties, action.timeout).pipe(
          map((openLoopFieldControl) =>
            startOpenLoopFieldControlStatus({ device, openLoopFieldControl, suppressToastr }),
          ),
          catchError(() => {
            const deviceName = this.deviceService.getDeviceName(device);

            return of(
              startOpenLoopFieldControlStatus({
                device,
                openLoopFieldControl: {
                  timeout: {
                    message: `Timeout while running the open loop field control on ${deviceName}.`,
                  },
                },
                suppressToastr,
              }),
            );
          }),
        );
      }),
    ),
  );

  handleOpenLoopFieldControlStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(startOpenLoopFieldControlStatus),
      tap((action) =>
        this.globalService.toastrStatusMessage(
          action.openLoopFieldControl,
          'Open Loop Field Control',
          action.suppressToastr,
        ),
      ),
      map((action) =>
        updateDevice({
          update: {
            changes: {
              openLoopFieldControlStatus: action.openLoopFieldControl,
            },
            id: action.device.serialNumber,
          },
        }),
      ),
    ),
  );

  //
  // Controller Auto-Tuning
  //

  /**
   * Request compute position or velocity auto-tuning gains using the existing tuning controller parameters.
   */
  handleRequestControllerAutoTuning$ = createEffect(() =>
    this.actions$.pipe(
      ofType(requestControllerAutoTuning),
      map(({ controllerType, device, timeout: aTimeout, suppressToastr }) => {
        const controller$ = this.deviceService.selectDeviceTuningControllerByType(device, controllerType);
        const controller = getCurrentValue(controller$);

        const parameters$ = this.tuningControllerService.selectTuningControllerParameters(controller);
        const parameters = getCurrentValue(parameters$);

        if (controllerType === 'position') {
          const positionParameters =
            mapParametersToFieldNameValues<MotionMasterMessage.Request.ComputeAutoTuningGains.IPositionParameters>(
              parameters,
            );

          positionParameters.settlingTime /= 1000;

          return computePositionAutoTuningGainsRequest({
            device,
            positionParameters,
            timeout: aTimeout,
            suppressToastr,
          });
        } else if (controllerType === 'velocity') {
          const velocityParameters =
            mapParametersToFieldNameValues<MotionMasterMessage.Request.ComputeAutoTuningGains.IVelocityParameters>(
              parameters,
            );

          return computeVelocityAutoTuningGainsRequest({
            device,
            velocityParameters,
            timeout: aTimeout,
            suppressToastr,
          });
        }
      }),
    ),
  );

  //
  // Update Device Configuration File Entries
  //

  handleUpdateDeviceConfigurationFileEntriesRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateDeviceConfigurationFileEntriesRequest),
      mergeMap(({ device, entries, timeout: aTimeout, suppressToastr }) => {
        const deviceName = this.deviceService.getDeviceName(device);

        return this.cn.client.request
          .getDeviceFile({ deviceAddress: device.deviceAddress, name: 'config.csv' }, aTimeout)
          .pipe(
            map((status) => {
              if (status.content) {
                const buffer = Uint8Array.from(status.content);
                const content = new TextDecoder().decode(buffer);
                const configFile = this.deviceService.parseDeviceConfigFileContent(device, content);

                entries.forEach((entry) => {
                  const row = configFile.rows.find((r) => r.index === entry.index && r.subindex === entry.subindex);
                  if (row) {
                    row.value = entry.value;
                  } else {
                    console.warn(
                      `No line in the config file on ${deviceName} that matches the entry: `,
                      JSON.stringify(entry),
                    );
                  }
                });

                return setDeviceFileRequest({
                  content: new TextEncoder().encode(configFile.rowsContent),
                  device,
                  filename: 'config.csv',
                  suppressToastr: { info: true, begin: true, success: true },
                  timeout: 5000,
                });
              } else {
                return updateDeviceConfigurationFileEntriesStatus({
                  device,
                  deviceConfigurationFileEntries: {
                    error: {
                      message: `No content in the config file on ${deviceName}.`,
                    },
                  },
                  suppressToastr,
                });
              }
            }),
            catchError(() => {
              return of(
                updateDeviceConfigurationFileEntriesStatus({
                  device,
                  deviceConfigurationFileEntries: {
                    timeout: {
                      message: `Timeout while updating the configuration file entries on ${deviceName}.`,
                    },
                  },
                  suppressToastr,
                }),
              );
            }),
          );
      }),
    ),
  );

  //
  // Circulo Encoder Configuration
  //

  dispatchStartCirculoEncoderConfigurationStatusOnRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(startCirculoEncoderConfigurationRequest),
      map((action) =>
        startCirculoEncoderConfigurationStatus({
          ...action,
          circuloEncoderConfiguration: {
            deviceAddress: action.device.deviceAddress,
            begin: {},
          },
        }),
      ),
    ),
  );

  handleStartCirculoEncoderConfigurationRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(startCirculoEncoderConfigurationRequest),
      mergeMap((action) => {
        const { device, encoderOrdinal, batteryModeMaxAcceleration, suppressToastr } = action;

        return this.cn.client.request
          .startCirculoEncoderConfiguration(
            { deviceAddress: device.deviceAddress, encoderOrdinal, batteryModeMaxAcceleration },
            action.timeout,
          )
          .pipe(
            map((circuloEncoderConfiguration) =>
              startCirculoEncoderConfigurationStatus({
                circuloEncoderConfiguration,
                device,
                suppressToastr,
              }),
            ),
            catchError(() => {
              const deviceName = this.deviceService.getDeviceName(device);

              return of(
                startCirculoEncoderConfigurationStatus({
                  circuloEncoderConfiguration: {
                    timeout: {
                      message: `Timeout while running the circulo encoder configuration on ${deviceName}.`,
                    },
                  },
                  device,
                  suppressToastr,
                }),
              );
            }),
          );
      }),
    ),
  );

  handleStartCirculoEncoderConfigurationStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(startCirculoEncoderConfigurationStatus),
      tap((action) =>
        this.globalService.toastrStatusMessage(
          action.circuloEncoderConfiguration,
          'Circulo Encoder Configuration',
          action.suppressToastr,
        ),
      ),
      map((action) =>
        updateDevice({
          update: {
            changes: {
              circuloEncoderConfigurationStatus: action.circuloEncoderConfiguration,
            },
            id: action.device.serialNumber,
          },
        }),
      ),
    ),
  );

  /**
   * Write the `circulo_integrated_encoder_<ordinal>_config.bin` file on the device
   * after the circulo encoder configuration or narrow angle calibration procedure is completed.
   * At the moment, there is an issue where eeprom data could be corrupted at some point in operation.
   * After we configure/calibrate encoder, we save the encoder configuration for the
   * target encoder on the device file system. When firmware detects that eeprom is corrupted,
   * it will load this file to restore the configuration.
   *
   * @link https://www.wrike.com/open.htm?id=1739960136
   */
  writeCirculoIntegratedEncoderConfigBinFileOnStartCirculoEncoderConfigurationStatusCompleted$ = createEffect(() => {
    type SupportedActions =
      | ReturnType<typeof startCirculoEncoderConfigurationStatus>
      | ReturnType<typeof startCirculoEncoderNarrowAngleCalibrationProcedureStatus>;

    const isConfigCompleted = (action: SupportedActions): boolean => {
      if (action.type === startCirculoEncoderConfigurationStatus.type) {
        return circuloEncoderConfigurationCompleted(action.circuloEncoderConfiguration);
      }
      return (
        !action.measurementOnly &&
        circuloEncoderNarrowAngleCalibrationProcedureCompleted(action.circuloEncoderNarrowAngleCalibrationProcedure)
      );
    };

    return this.actions$.pipe(
      ofType(startCirculoEncoderConfigurationStatus, startCirculoEncoderNarrowAngleCalibrationProcedureStatus),
      filter(isConfigCompleted),
      mergeMap((action) => {
        const device = action.device;
        const status =
          action.type === startCirculoEncoderConfigurationStatus.type
            ? action.circuloEncoderConfiguration
            : action.circuloEncoderNarrowAngleCalibrationProcedure;

        const isCalibrationFlow = action.type === startCirculoEncoderNarrowAngleCalibrationProcedureStatus.type;

        const buildFailureActions = () => {
          const failureActions = [writeCirculoEncoderConfigFailed()];
          if (isCalibrationFlow) {
            failureActions.push(
              updateDevice({
                update: { changes: { circuloEncoderConfigurationBinWriteRunning: false }, id: device.serialNumber },
              }) as any,
            );
          }
          return from(failureActions);
        };

        return this.cn.client.request
          .writeCirculoIntegratedEncoderConfigBinFile(device.serialNumber, status.encoderOrdinal)
          .pipe(
            mergeMap((deviceFileStatus) => {
              if (deviceFileStatus.error) {
                this.toastr.error(deviceFileStatus.error.message, 'Writing Encoder Configuration Binary File');
                return buildFailureActions();
              }

              return of(
                triggerCirculoRegisterReadAfterWritingEncoderConfigBin({
                  device,
                  encoderOrdinal: status.encoderOrdinal,
                  flow: isCalibrationFlow ? 'calibration' : 'configuration',
                }),
              );
            }),
            catchError((err) => {
              this.toastr.error(err, 'Writing Encoder Configuration Binary File');
              return buildFailureActions();
            }),
          );
      }),
    );
  });

  /**
   * Triggers a read of encoder register `0x11` after writing the
   * `circulo_integrated_encoder_<ordinal>_config.bin` file to the device.
   *
   * The purpose of this read is to verify that the new encoder configuration
   * has been successfully applied.
   *
   * Register `0x11` of the iC-MU is `OUT_ZERO_OUT_MSB`. Its `OUT_MSB(4:0)` selects which bit
   * of the encoder's internal 38-bit position word is transmitted as the most significant, so
   * the width of the transmitted word is 14 + OUT_MSB: an 18-bit output reads 0x04, a 19-bit
   * output 0x05, a 20-bit output 0x06, and a 19-bit output with 18 multiturn bits on top of it
   * reads 0x17. Its factory default is 0xA5, which no Circulo configuration writes, so that
   * value means the encoder has never been configured.
   *
   * This information is used to detect configuration mismatches, specifically to warn when
   * the encoder was programmed for a different output resolution than the drive is set up
   * for - a single-turn encoder where multiturn was asked for, most of all.
   *
   * @see deviceInternalEncoderConfigurationStatus
   */
  readEncoderRegisterAfterWriteCirculoIntegratedEncoderConfigBinCompleted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(triggerCirculoRegisterReadAfterWritingEncoderConfigBin),
      map(({ device, encoderOrdinal, flow }) =>
        readEncoderRegisterRequest({
          device,
          address: 0x11,
          encoderOrdinal,
          timeout: 1000,
          flow,
        }),
      ),
    ),
  );

  //
  // Encoder Register
  //

  handleReadEncoderRegisterRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(readEncoderRegisterRequest),
      mergeMap(({ device, encoderOrdinal, address, flow }) =>
        this.cn.client.request
          .runEncoderRegisterCommunicationOsCommand(
            device.serialNumber,
            encoderOrdinal,
            0, // slaveAddress
            0, // rw = read
            address, // registerAddress
          )
          .pipe(
            filter((osCommand) => osCommand.request === 'succeeded' || osCommand.request === 'failed'),
            mergeMap((osCommand) => {
              let readEncoderRegisterResponse = device.readEncoderRegisterResponse ?? {};

              if (osCommand.request === 'succeeded') {
                const data = osCommand.data;

                const response: OsCommandResponse = {
                  data,
                  request: osCommand.request,
                  response: osCommand.response,
                };

                readEncoderRegisterResponse = _.merge(readEncoderRegisterResponse, {
                  [encoderOrdinal]: {
                    [address]: response,
                  },
                });
              } else {
                const message = `Error reading encoder register 0x${address.toString(16)}: ${osCommand.errorDescription || 'Unknown error'}`;
                this.toastr.error(message, 'Read Encoder Register', { disableTimeOut: true });
                readEncoderRegisterResponse = _.merge(readEncoderRegisterResponse, {
                  [encoderOrdinal]: {
                    [address]: null,
                  },
                });
              }

              const changes: Partial<Device> = { readEncoderRegisterResponse };
              if (flow === 'calibration') {
                changes.circuloEncoderConfigurationBinWriteRunning = false;
              }

              return from([
                updateDevice({
                  update: {
                    changes,
                    id: device.serialNumber,
                  },
                }),
                readEncoderRegisterCompleted({ flow }),
              ]);
            }),
          ),
      ),
    ),
  );

  completeConfigurationFlowAfterReadingEncoderRegister$ = createEffect(() =>
    this.actions$.pipe(
      ofType(readEncoderRegisterCompleted),
      filter((action) => action.flow === 'configuration'),
      map(() => circuloEncoderConfigurationProcedureCompleted()),
    ),
  );

  completeCalibrationFlowAfterReadingEncoderRegister$ = createEffect(() =>
    this.actions$.pipe(
      ofType(readEncoderRegisterCompleted),
      filter((action) => action.flow === 'calibration'),
      map(() => circuloEncoderCalibrationProcedureCompleted()),
    ),
  );

  integroEncoderCalibrationRunning$ = createEffect(() =>
    this.actions$.pipe(
      ofType(integroEncoderCalibrationProcedureRunning),
      map(({ deviceSerialNumber, isRunning }) => {
        return updateDevice({
          update: {
            changes: {
              integroEncoderCalibrationProcedureRunning: isRunning,
            },
            id: deviceSerialNumber,
          },
        });
      }),
    ),
  );

  updateSystemIdentificationParametersFromPlantModel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateSystemIdentificationParametersValuesFromPlantModel),
      mergeMap(({ device }) =>
        this.deviceService.selectDeviceFile(device, 'plant_model.csv').pipe(
          filter((plantModel) => !!plantModel?.content && plantModel.content.length),
          map((plantModel) => parsePlantModelCsv(plantModel?.content)),
          map((parsedPlantModel) => {
            const entries = Object.entries(parsedPlantModel);

            const updates = entries.map(([key, value]) => {
              const id = `${key}.${device.serialNumber}`;
              return {
                changes: {
                  value,
                },
                id,
              };
            });

            return updateSystemIdentificationParameters({ updates });
          }),
        ),
      ),
    ),
  );

  checkCommandExecutedOnSlave(
    device: Device,
    parameterValues: { index: number; subindex: number; uintValue: number; intValue: number }[],
    requestTimeout = 4000,
  ) {
    const deviceName = this.deviceService.getDeviceName(device);

    return new Observable<MotionMasterMessage.Status.IDeviceParameterValues>((subscriber) => {
      this.cn.client.request
        .getDeviceParameterValues({ deviceAddress: device.deviceAddress, parameters: parameterValues }, requestTimeout)
        .pipe(
          map((deviceParameterValues) => {
            const parameter = deviceParameterValues.parameterValues[0];
            if (parameter.error) {
              subscriber.error(
                new Error(
                  `Failed to get the value of ${parameter.index.toString(16)}:${parameter.subindex} from ${deviceName}.`,
                ),
              );
            } else if (parameter.uintValue !== 1) {
              subscriber.error(
                new Error(
                  `Timeout waiting for the value of ${parameter.index.toString(16)}:${parameter.subindex} from ${deviceName}.`,
                ),
              );
            } else {
              subscriber.next(deviceParameterValues);
              subscriber.complete();
            }
          }),
        )
        .subscribe(); // executes the observable to get the parameter values
    });
  }

  getStackImageFromDeviceAndMapToUpdateProductImage(device: Device) {
    return this.cn.client.request
      .getDeviceFile({ deviceAddress: device.deviceAddress, name: 'stack_image.svg.zip' }, 60000)
      .pipe(
        map((status) => {
          let svg: string;
          if (status.content) {
            const buffer = Uint8Array.from(status.content);
            svg = new TextDecoder().decode(buffer);
          }
          return updateDevice({
            update: {
              changes: {
                productImage: svg,
              },
              id: device.serialNumber,
            },
          });
        }),
      );
  }
}
